import { useEffect, useRef, RefObject, MouseEvent } from 'react';
import { HomefieldIconSignCancel } from '@dsg-tech/homefield-react-icons';
import useOutsideAlerter from 'hooks/useOutsideAlerter';
import { HeaderFeaturedTaxonomy, ProgressiveHeaderTaxonomy } from 'types';
import {
  progressiveHeaderTaxoSelector,
  headerFeaturedTaxoSelector,
} from 'store/selectors/taxonomySelector';
import { useAppSelector } from 'store/store';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import FlexL1Columns, { L1CategoryData } from './FlexL1Columns';
import { NEW_KEY_CODES } from '../../../../../utility/constants';

export const getL0Title = (headerTaxo: ProgressiveHeaderTaxonomy, currentCategoryIndex: number) => {
  const title = headerTaxo.Taxonomy2?.[currentCategoryIndex].title;
  if (title) {
    return title;
  }
  return null;
};

export const getL1Categories = (
  headerTaxo: ProgressiveHeaderTaxonomy,
  featuredTaxo: HeaderFeaturedTaxonomy,
  currentCategoryIndex: number,
) => {
  const taxoData = headerTaxo.Taxonomy2;
  const isValid =
    taxoData &&
    Array.isArray(taxoData) &&
    taxoData.length > currentCategoryIndex &&
    taxoData[currentCategoryIndex]?.csvData &&
    Array.isArray(taxoData[currentCategoryIndex].csvData) &&
    taxoData[currentCategoryIndex].csvData.length > 0 &&
    taxoData[currentCategoryIndex].csvData[0]?.col &&
    Array.isArray(taxoData[currentCategoryIndex].csvData[0].col);
  if (!isValid) {
    return null;
  }

  const taxoLink = taxoData[currentCategoryIndex].link;

  const headerColumnCats = taxoData[currentCategoryIndex].csvData[0].col[0].cats;
  let headerCategories: L1CategoryData[] = headerColumnCats.map((cat) => {
    const title = cat.n;
    const link = cat.u;
    const l2Categories =
      cat.ch?.map((subcat) => {
        const subcatTitle = subcat.n;
        const subcatLink = subcat.u;
        return { title: subcatTitle, link: subcatLink };
      }) || [];
    return { title, link, l2Categories };
  });

  const featuredLink =
    featuredTaxo?.featuredLink?.find((link) => link.l0SeoUrl === taxoLink) ?? null;
  if (!featuredLink) {
    return headerCategories;
  }

  // if we have featured link ctas, add those to the beginning of the headers
  if (
    Array.isArray(featuredLink.cta) &&
    featuredLink.cta?.length > 0 &&
    Object.keys(featuredLink.cta[0]).length > 0
  ) {
    const beginningFeaturedCategory: L1CategoryData = {
      l2Categories: featuredLink.cta.map((link) => ({
        // make sure this exists
        title: link.linkTitle,
        link: link.link,
      })),
    };
    headerCategories = [beginningFeaturedCategory, ...headerCategories];
  }

  // if we have featured-link beta-ctas, add those to the end of the list
  if (
    Array.isArray(featuredLink['cta-beta']) &&
    featuredLink['cta-beta']?.length > 0 &&
    Object.keys(featuredLink['cta-beta'][0]).length > 0
  ) {
    const endingFeaturedCategory: L1CategoryData = {
      title: featuredLink.featuredFlyoutLabel,
      link: featuredLink.l0SeoUrl,
      l2Categories: featuredLink['cta-beta'].map((link) => ({
        // make sure this exists
        title: link.linkTitle,
        link: link.link,
      })),
    };
    headerCategories = [...headerCategories, endingFeaturedCategory];
  }

  return headerCategories;
};

const ProgressiveMenuContainer = ({
  showSubMenu,
  currentCategoryIndex,
  closeMenu,
  changeFocus,
  handleMouseLeaveNavigation,
  firstSubLinkRef,
}: {
  currentCategoryIndex: number;
  showSubMenu: boolean;
  closeMenu: () => void;
  changeFocus: (offset?: number) => void;
  handleMouseLeaveNavigation: () => void;
  firstSubLinkRef: RefObject<HTMLAnchorElement | null>;
}) => {
  const wrapperRef = useRef(null);
  const handleMenuClose = (e: MouseEvent<HTMLElement, 'click'>) => {
    const targetId = 'id' in e.target && typeof e.target.id === 'string' ? e.target.id : '';
    if (!targetId.includes('desktop-nav-l0')) {
      closeMenu();
    }
  };
  useOutsideAlerter(wrapperRef, handleMenuClose);

  useEffect(() => {
    const pageHeight = document.body.scrollHeight;
    const parentContainer = document.getElementsByClassName(
      'desktop-nav-container',
    )[0] as HTMLElement;
    const menuContainer = parentContainer.getElementsByClassName(
      'menu-container',
    )[0] as HTMLElement;
    // exit early if the menu container isn't found. This sometimes happens in cases where
    // there's no taxoData. In those instances, the dropdown menu doesn't exist
    if (!menuContainer) {
      return;
    }
    if (showSubMenu) {
      menuContainer.style.height = `${pageHeight}px`;
    } else {
      menuContainer.style.height = 'unset';
    }
  }, [showSubMenu]);

  const progressiveHeaderTaxoContent = useAppSelector(progressiveHeaderTaxoSelector);
  const headerFeaturedTaxoContent = useAppSelector(headerFeaturedTaxoSelector);
  const l1Categories = getL1Categories(
    progressiveHeaderTaxoContent,
    headerFeaturedTaxoContent,
    currentCategoryIndex,
  );
  const l0Title = getL0Title(progressiveHeaderTaxoContent, currentCategoryIndex);

  // exit early if data wasn't properly structured
  if (!(l1Categories && l0Title)) {
    return null;
  }

  return (
    <div className="menu-container">
      {showSubMenu && <div className="background-overlay" />}
      <div
        role="menu"
        className={`desktop-nav-container__menu hmf-pt-m hmf-pb-s hmf-pl-m${
          showSubMenu ? ' hmf-display-flex' : ' hmf-display-none'
        }`}
        ref={wrapperRef}
        onMouseLeave={handleMouseLeaveNavigation}
        tabIndex={0}>
        <div className="dynamic-category-wrapper hmf-w-100">
          <ErrorBoundaryWithLogger componentId="DesktopNav-FlexL1Columns">
            <FlexL1Columns
              l0Title={l0Title}
              l1Categories={l1Categories}
              changeFocus={changeFocus}
              firstSubLinkRef={firstSubLinkRef}
            />
          </ErrorBoundaryWithLogger>
        </div>
        <button
          className="close-button"
          aria-label="Close Menu"
          type="button"
          onClick={() => closeMenu()}
          onKeyDown={(event) => {
            if (event.key === ' ' || event.key === NEW_KEY_CODES.ENTER) {
              closeMenu();
            }
          }}
          tabIndex={0}>
          <HomefieldIconSignCancel
            className="close-button__icon hmf-mt-xs"
            height="22px"
            width="22px"
          />
          <span className="close-button__text hmf-subheader-s hmf-m-xxs">Close</span>
        </button>
      </div>
    </div>
  );
};

export default ProgressiveMenuContainer;
